import { Localized } from "@fluent/react"
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material"
import { useMemo, useState } from "react"

import { SaveButton } from "../../../../app/components/SaveButton"
import { CloseButton } from "../../../../app/components/CloseButton"
import { AppId } from "../../../../app/appTypes"
import { useAppDispatch } from "../../../../app/hooks"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import {
    changeWorkflowActionDeadline,
    DocumentWorkflowDefinitionDto,
    DocumentWorkflowStageDto,
} from "../documentWorkflowApi"
import { mapWorkflowActionStatus } from "../documentWorkflowHelpers"

export type ChangeWorkflowActionDeadlineMode = "all" | "selected"

export interface ChangeWorkflowActionDeadlineDialogProps {
    documentId: AppId
    stage: DocumentWorkflowStageDto
    mode: ChangeWorkflowActionDeadlineMode
    selectedTeamMemberId?: number | null
    onClose: () => void
    onSuccess: (definition: DocumentWorkflowDefinitionDto) => void
}

const getLocalISODate = (date: Date | number): string => {
    const value = typeof date === "number" ? new Date(date) : date
    const tzOffset = value.getTimezoneOffset() * 60000

    return new Date(value.getTime() - tzOffset)
        .toISOString()
        .split("T")[0]
}

const getDefaultDeadline = (): string => {
    const date = new Date()
    date.setDate(date.getDate() + 7)

    return getLocalISODate(date)
}

export const ChangeWorkflowActionDeadlineDialog = ({
    documentId,
    stage,
    mode,
    selectedTeamMemberId,
    onClose,
    onSuccess,
}: ChangeWorkflowActionDeadlineDialogProps) => {
    const dispatch = useAppDispatch()
    const today = getLocalISODate(new Date())
    const [deadline, setDeadline] = useState(getDefaultDeadline)
    const [isSaving, setIsSaving] = useState(false)
    const [serverError, setServerError] = useState("")

    const waitingMembers = useMemo(() => {
        return stage.members.filter(member => mapWorkflowActionStatus(member.actionStatus).waiting)
    }, [stage.members])

    const affectedMembersCount = useMemo(() => {
        if (mode === "all") {
            return waitingMembers.length
        }

        const isSelectedMemberWaiting = waitingMembers.some(
            member => member.documentTeamMemberId === selectedTeamMemberId
        )

        return isSelectedMemberWaiting ? 1 : 0
    }, [mode, selectedTeamMemberId, waitingMembers])

    const selectedMember = useMemo(() => {
        if (mode !== "selected") {
            return null
        }

        return stage.members.find(
            member => member.documentTeamMemberId === selectedTeamMemberId
        ) ?? null
    }, [mode, selectedTeamMemberId, stage.members])

    const deadlineError = deadline.trim() === "" || deadline < today

    const handleSaveClick = async () => {
        if (deadlineError || isSaving || affectedMembersCount === 0) {
            return
        }

        const forAllMembers = mode === "all"
        setIsSaving(true)
        setServerError("")

        try {
            const result = await dispatch(
                changeWorkflowActionDeadline({
                    documentId,
                    dto: {
                        stageId: String(stage.id),
                        deadline,
                        documentTeamMemberId: forAllMembers
                            ? null
                            : selectedTeamMemberId !== null && selectedTeamMemberId !== undefined
                                ? String(selectedTeamMemberId)
                                : undefined,
                        forAllMembers,
                    },
                })
            )

            if (changeWorkflowActionDeadline.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))
                onSuccess(result.payload)
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem
                    if (problem?.title) {
                        errorMsg = problem.title
                    }
                }
                setServerError(errorMsg)
            }
        } catch {
            dispatch(showError("error"))
        } finally {
            setIsSaving(false)
        }
    }

    return <Dialog open={true} fullWidth maxWidth="xs">
        <DialogTitle>
            <Localized id="document-change-deadline">
                <span>_Zmiana terminu akcji workflow</span>
            </Localized>
        </DialogTitle>

        <DialogContent>
            {mode === "all"
                ? <Alert severity="warning" variant="outlined" sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        <Localized id="workflow-change-deadline-all-confirmation">
                            <span>_Czy napewno przedluzyc termin dla wszystkich oczekujacych osob?</span>
                        </Localized>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        <Localized
                            id="workflow-change-deadline-affected-count"
                            vars={{ count: affectedMembersCount }}
                        >
                            <span>_Liczba osob objetych zmiana: {affectedMembersCount}.</span>
                        </Localized>
                    </Typography>
                </Alert>
                : <Alert severity="info" variant="outlined" sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        <Localized id="workflow-change-deadline-selected-confirmation">
                            <span>_Zmiana terminu zostanie wykonana tylko dla wybranej osoby.</span>
                        </Localized>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedMember
                            ? <Localized
                                id="workflow-change-deadline-selected-member"
                                vars={{ memberName: selectedMember.fullName || selectedMember.userName }}
                            >
                                <span>_Akcja zostanie uruchomiona dla: {selectedMember.fullName || selectedMember.userName}.</span>
                            </Localized>
                            : <Localized id="workflow-change-deadline-selected-member-not-found">
                                <span>_Nie znaleziono wybranej osoby w tym etapie.</span>
                            </Localized>}
                    </Typography>
                </Alert>}

            <TextField
                fullWidth
                required
                type="date"
                value={deadline}
                onChange={event => setDeadline(event.target.value)}
                inputProps={{
                    min: today,
                }}
                error={deadlineError}
                helperText={
                    deadlineError
                        ? <Localized id="deadline-not-in-past">
                            <span>_Termin nie moze byc wczesniejszy niz dzisiaj</span>
                        </Localized>
                        : " "
                }
                label={
                    <Localized id="deadline">
                        <span>_Termin</span>
                    </Localized>
                }
                InputLabelProps={{ shrink: true }}
            />

            {serverError && <Alert sx={{ mt: 1 }} onClose={() => setServerError("")} severity="error">
                <Localized id={serverError}>
                    <span>_Server error</span>
                </Localized>
            </Alert>}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
            <SaveButton
                loading={isSaving}
                onClick={handleSaveClick}
                disabled={deadlineError || affectedMembersCount === 0 || (mode === "selected" && !selectedMember)}
            />
            <CloseButton loading={isSaving} onClick={onClose} />
        </DialogActions>
    </Dialog>
}
